import classNames from 'classnames'
import { isAppLut } from 'legacy/lut/functions'
import { IState } from 'legacy/lut/store'
import { parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js'
import React, { JSX } from 'react'
import { connect } from 'react-redux'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { IReference } from 'core/classes/models/IReference'
import { CALL_PROGRAMS_LIST_PROTOCOLS } from 'core/constants/callListProtocols'
import { MY_CONNECT_TYPE } from 'dialer/classes/models/MyConnectType'
import {
  CALL_MODE,
  CallablePhoneWrapper,
} from 'dialer/components/widgets/public/callPhoneWrapper/CallablePhoneWrapper'

import styles from './PhoneCall.module.scss'

interface IProps {
  protocol?: string
  value?: string
  icon?: JSX.Element
  countryCode?: string
  guid?: string
  isContactLevel?: boolean
  className?: string
  onClick?: (e: any) => void
  partnerId?: string
  isColor?: boolean
  children?: React.ReactNode
  isDisabled?: boolean
}

const protocolFind = (i: IReference, protocol: string) =>
  i.value.toLowerCase() === protocol.toLowerCase()

const PhoneCall = React.memo(
  ({
    protocol,
    value,
    icon,
    countryCode,
    guid,
    className,
    onClick,
    isContactLevel,
    partnerId,
    isColor,
    children,
    isDisabled,
  }: IProps) => {
    if (!value) {
      return <span />
    }
    let protocolRef
    if (protocol) {
      protocolRef = CALL_PROGRAMS_LIST_PROTOCOLS.find((i) => protocolFind(i, protocol as string))
    }
    if (!protocolRef) {
      protocolRef = CALL_PROGRAMS_LIST_PROTOCOLS.find((i) => protocolFind(i, 'tel://'))
    }
    const guidAvailable = protocolRef && protocolRef.value
    let protocolHref = 'tel://'
    let phoneNumber: PhoneNumber | undefined
    const useGuid =
      guidAvailable && guid
        ? `?recordId=${guid}&recordModule=${isContactLevel ? 'contact' : 'account'}`
        : ''
    const clearNumber = value.replace(/[\D]/g, '')
    if (protocolRef) {
      if (countryCode) {
        protocolHref = `${protocolRef.value}+${countryCode}${value}${useGuid}`
      } else {
        phoneNumber = parsePhoneNumberFromString(`+${clearNumber}`)
        if (phoneNumber) {
          protocolHref = `${protocolRef.value}${phoneNumber.number}${useGuid}`
        } else {
          protocolHref = `${protocolRef.value}+${clearNumber}${useGuid}`
        }
      }
    }
    let caption
    if (children) {
      caption = children
    } else if (icon) {
      caption = icon
    } else {
      caption = countryCode
        ? `+(${countryCode}) ${value}`
        : phoneNumber
          ? phoneNumber.formatInternational()
          : value
    }

    return (
      <>
        <RenderWhen condition={!isDisabled}>
          <CallablePhoneWrapper
            callMode={CALL_MODE.byPhoneClick}
            phone={value}
            type={MY_CONNECT_TYPE.PartnerContact}
            replacementContent={
              <span className={classNames(className, styles.root, { [styles.gray]: isColor })}>
                {caption}
              </span>
            }
            additionalAttributes={{
              partnerId,
              contactProcessedInScout: isAppLut(),
            }}
          >
            <a
              onClick={onClick}
              className={classNames(className, styles.root, { [styles.gray]: !isColor })}
              href={`${protocolHref}`}
            >
              {caption}
            </a>
          </CallablePhoneWrapper>
        </RenderWhen>
        <RenderWhen condition={isDisabled}>
          <span
            className={classNames(className, styles.root, { [styles.gray]: !isColor })}
            data-disabled={isDisabled}
          >
            {caption}
          </span>
        </RenderWhen>
      </>
    )
  }
)

const mapStateToProps = (
  state: IState,
  props: Pick<
    IProps,
    | 'value'
    | 'className'
    | 'onClick'
    | 'guid'
    | 'countryCode'
    | 'icon'
    | 'isContactLevel'
    | 'partnerId'
    | 'isColor'
    | 'children'
    | 'isDisabled'
  >
): IProps => ({
  value: props.value,
  icon: props.icon,
  countryCode: props.countryCode,
  guid: props.guid,
  className: props.className,
  onClick: props.onClick,
  isContactLevel: props.isContactLevel,
  isColor: props.isColor,
  children: props.children,
  isDisabled: props.isDisabled,
})

const PhoneCallConnected = connect(mapStateToProps)(PhoneCall)
export { PhoneCallConnected as PhoneCall }
